import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HardHat, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen dark bg-background text-foreground grid lg:grid-cols-2">
      <div className="hidden lg:block relative blueprint-grid border-r border-border">
        <div className="absolute inset-0 grid place-items-center p-12">
          <div className="max-w-md">
            <div className="h-12 w-12 rounded-xl bg-primary grid place-items-center mb-6"><HardHat className="h-6 w-6 text-primary-foreground" /></div>
            <h2 className="text-3xl font-extrabold tracking-tight">Precision estimates, powered by AI.</h2>
            <p className="mt-4 text-muted-foreground">Sign in to your command center for BOQs, remodels and bid analytics.</p>
          </div>
        </div>
      </div>
      <div className="grid place-items-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h1 className="text-2xl font-bold tracking-tight mb-1">Sign in</h1>
          <p className="text-sm text-muted-foreground mb-6">Access your SmartBuild workspace.</p>

          <button type="button" onClick={googleLogin} data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 rounded-lg border border-border bg-card py-2.5 font-semibold hover:bg-secondary transition-colors mb-4">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
            Continue with Google
          </button>
          <div className="flex items-center gap-3 my-4 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
          </div>

          <label className="block text-sm font-semibold mb-1.5">Email</label>
          <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-input bg-card px-3 py-2.5 mb-4 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition" placeholder="you@company.com" />
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-semibold">Password</label>
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-primary font-semibold hover:underline">Forgot password?</Link>
          </div>
          <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-input bg-card px-3 py-2.5 mb-6 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition" placeholder="••••••••" />

          <button data-testid="login-submit-btn" disabled={loading}
            className="w-full rounded-lg bg-primary text-primary-foreground py-2.5 font-bold hover:bg-orange-600 active:bg-orange-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </button>
          <p className="text-sm text-muted-foreground mt-5 text-center">
            No account? <Link to="/register" className="text-primary font-semibold hover:underline" data-testid="to-register-link">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
